import { AxiosError } from 'axios';
import {
  BaseSyntheticEvent,
  createContext,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useForm, UseFormMethods } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';

import { MediaAPI } from 'services/media.api';
import { QuizAPI } from 'services/quiz.api';
import {
  IQuiz,
  IQuizFormInputGroup,
  IQuizFormValuePayload,
  IQuizFormValues,
  IQuizSubmitPayload,
} from './quiz.types';
import { quizQueryKeys } from './useQuizQueries';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build the flat list of "steps" from the quiz's formInputGroups.
 * Each FormInputGroup = 1 step regardless of its `isDeclarative` flag.
 */
export const buildSteps = (quiz: IQuiz): IQuizFormInputGroup[] =>
  quiz.formInputGroups ?? [];

/**
 * Build the initial RHF default values: one answer slot per FormInput id.
 */
export const buildDefaultValues = (
  groups: IQuizFormInputGroup[],
): IQuizFormValues => {
  const answers: IQuizFormValues['answers'] = {};
  groups.forEach((group) => {
    group.formInputs.forEach((input) => {
      answers[input.id] = { value: '', choiceValues: [], file: null };
    });
  });
  return { answers };
};

// ---------------------------------------------------------------------------
// Context (consumed by QuizNavigation)
// ---------------------------------------------------------------------------

export type QuizContextType = {
  currentStep: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  /** Navigate to previous step */
  goPrevStep: () => void;
  /** Validate current step then navigate forward (or open confirm dialog on last step) */
  goNextStep: () => void;
  /** Whether the current step has been interacted with */
  isCurrentStepValid: boolean;
  /** True from the click on "Valider" until the API answered: blocks a 2nd send */
  isSubmitting: boolean;
  /** Opens the submit confirmation (set true on last step "Valider" click) */
  openConfirmDialog: boolean;
  setOpenConfirmDialog: (v: boolean) => void;
};

export const QuizContext = createContext<QuizContextType>(undefined!);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseQuizFormReturn {
  /** Current step index (0-based) */
  currentStep: number;
  /** All steps (one per FormInputGroup) */
  steps: IQuizFormInputGroup[];
  /** Computed total displayed to the user (1-based) */
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  goNext: () => void;
  goPrev: () => void;
  goToStep: (index: number) => void;
  /** RHF form instance */
  form: UseFormMethods<IQuizFormValues>;
  /** Submit the whole form */
  handleSubmit: (event?: BaseSyntheticEvent) => void;
  /** True while the answers are being uploaded then POSTed to the API */
  isSubmitting: boolean;
  isSubmitSuccess: boolean;
  isSubmitError: boolean;
  submitError: AxiosError | null;
  /** Whether all required fields on the current step are satisfied */
  isCurrentStepValid: boolean;
  /** Set of step indices that have been validated (next was successfully triggered) */
  validatedSteps: Set<number>;
  openConfirmDialog: boolean;
  setOpenConfirmDialog: (v: boolean) => void;
  /** Reset the quiz to the first step so the user can answer again */
  resetQuiz: () => void;
}

export const useQuizForm = (quiz: IQuiz): UseQuizFormReturn => {
  const steps = buildSteps(quiz);
  const totalSteps = steps.length;

  const [currentStep, setCurrentStep] = useState(0);
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [validatedSteps, setValidatedSteps] = useState<Set<number>>(new Set());

  const form = useForm<IQuizFormValues>({
    defaultValues: buildDefaultValues(steps),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // ---- Current step validity (drives the "next" button disabled state) ----
  const currentGroup = steps[currentStep];
  const allWatchedValues = form.watch();
  const watchedAnswers = allWatchedValues?.answers;
  // Only required inputs block the "Next" button. Non-required questions are
  // always considered valid regardless of how much (or how little) the user
  // has filled in — the same rule that governs RHF validation at submit time.
  const isCurrentStepValid = (currentGroup?.formInputs ?? [])
    .filter((i) => i.isRequired)
    .every((input) => {
      const answer = watchedAnswers?.[input.id];
      if (!answer) return false;
      if (input.type === 'text') return !!answer.value?.trim();
      if (input.type === 'upload') return answer.file instanceof File;
      // choice / scale
      const minAnswer = input.minAnswer ?? 1;
      if (
        Array.isArray(answer.choiceValues) &&
        answer.choiceValues.length >= minAnswer &&
        minAnswer > 0
      )
        return true;
      return !!answer.value?.trim();
    });

  // ---- Navigation ----------------------------------------------------------

  const goNext = useCallback(() => {
    setValidatedSteps((prev) => new Set(prev).add(currentStep));
    setCurrentStep((s) => Math.min(s + 1, totalSteps - 1));
  }, [currentStep, totalSteps]);

  const goPrev = useCallback(() => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  }, []);

  const goToStep = useCallback(
    (index: number) => {
      setCurrentStep(Math.max(0, Math.min(index, totalSteps - 1)));
    },
    [totalSteps],
  );

  // ---- Submission ----------------------------------------------------------

  const queryClient = useQueryClient();

  const mutation = useMutation<unknown, AxiosError, IQuizSubmitPayload>(
    (payload) => QuizAPI.submitEntry(payload),
    {
      onSuccess: () => {
        // Refresh latestEntry immediately after a successful submit so QuizPage
        // has up-to-date data (isAlreadyAnswered, formValues…).
        queryClient.invalidateQueries(quizQueryKeys.latestEntry(quiz.id));
      },
    },
  );

  const resetQuiz = useCallback(() => {
    mutation.reset();
    form.reset(buildDefaultValues(steps));
    setCurrentStep(0);
    setValidatedSteps(new Set());
    queryClient.invalidateQueries(quizQueryKeys.latestEntry(quiz.id));
  }, [form, mutation, queryClient, quiz.id, steps]);

  /**
   * Build the API payload from RHF values, uploading any pending files first,
   * then POST the entry.
   */
  const submitForm = form.handleSubmit(
    // onValid
    async (values) => {
      const formValues: IQuizFormValuePayload[] = [];

      for (const group of steps) {
        for (const input of group.formInputs) {
          const answer = values.answers[input.id];
          if (!answer) continue;

          const payload: IQuizFormValuePayload = {
            formInput: input['@id'],
          };

          if (input.type === 'text') {
            payload.value = answer.value || null;
          } else if (input.type === 'upload') {
            if (answer.file) {
              const uploadResponse: any = await MediaAPI.postMedia(
                answer.file,
                'FormEntry',
              );
              payload.image = uploadResponse?.data?.['@id'] ?? null;
            }
          } else if (input.type === 'choice') {
            // Non-required choice with no selection: omit the formValue entirely
            // so the API doesn't run its minAnswer validation against an empty array.
            if (!input.isRequired && answer.choiceValues.length === 0) continue;
            payload.formInputChoiceValues = answer.choiceValues.map((iri) => ({
              formInputChoice: iri,
            }));
          }

          formValues.push(payload);
        }
      }

      const submitPayload: IQuizSubmitPayload = {
        form: quiz['@id'],
        formValues,
      };

      // `mutateAsync` (instead of `mutate`) keeps RHF's `isSubmitting` true for
      // the whole round-trip: the promise returned by `submitForm` only settles
      // once the API answered. The rejection is swallowed here because the error
      // state is already carried by `mutation.isError`.
      try {
        await mutation.mutateAsync(submitPayload);
      } catch {
        // handled through mutation.isError / submitError
      }
    },
    // onInvalid — RHF appelle ce callback quand la validation échoue au submit.
    // On cherche le premier step qui contient un champ en erreur et on y navigue,
    // car les steps cachées (display:none) sont montées mais leurs erreurs invisibles.
    (formErrors) => {
      const erroredInputIds = Object.keys(formErrors?.answers ?? {});
      if (erroredInputIds.length === 0) return;

      const firstErroredStepIndex = steps.findIndex((group) =>
        group.formInputs.some((input) =>
          erroredInputIds.includes(String(input.id)),
        ),
      );

      if (firstErroredStepIndex !== -1) {
        setCurrentStep(firstErroredStepIndex);
      }
    },
  );

  /**
   * Re-entrancy guard against a double-click on the validation button.
   * The button is disabled as soon as a submission starts, but the render that
   * applies `disabled` only happens after the first click handler returned:
   * this ref closes that window (plus any programmatic re-trigger) so a second
   * entry can never be sent while the first request is still in flight.
   */
  const isSubmittingRef = useRef(false);

  const handleSubmit = async (event?: BaseSyntheticEvent): Promise<void> => {
    if (isSubmittingRef.current) {
      if (event) event.preventDefault();
      return;
    }
    isSubmittingRef.current = true;
    try {
      await submitForm(event);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return {
    currentStep,
    steps,
    totalSteps,
    isFirstStep,
    isLastStep,
    goNext,
    goPrev,
    goToStep,
    form,
    handleSubmit,
    // RHF's flag covers the whole onValid callback (file uploads included), the
    // mutation flag keeps it true should the two ever settle out of sync.
    isSubmitting: form.formState.isSubmitting || mutation.isLoading,
    isSubmitSuccess: mutation.isSuccess,
    isSubmitError: mutation.isError,
    submitError: mutation.error ?? null,
    isCurrentStepValid,
    validatedSteps,
    openConfirmDialog,
    setOpenConfirmDialog,
    resetQuiz,
  };
};
